import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BASIC_COLOURS, TRADING_VARIANTS } from '../../fundamentals/colours/variants';
import { DEFAULT_ICON_SIZE, useAssetIconUrl, useIconBaseCss } from '../Icon/Icon';
import { IGutterAsMarginProps } from '../../fundamentals/spaces/space';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { MATERIAL_ICONS } from '../Icon/icons';
import { useFilterColourCss } from '../../fundamentals/colours/filter';
import { ColourAssignment, IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseImg } from '../../common/BaseElements';

export const TRIANGLE_INDICATOR_VARIANTS = {
  POSITIVE: TRADING_VARIANTS.POSITIVE,
  NEGATIVE: TRADING_VARIANTS.NEGATIVE,
  ...BASIC_COLOURS
} as const;

export type TriangleIndicatorVariant = (typeof TRIANGLE_INDICATOR_VARIANTS)[keyof typeof TRIANGLE_INDICATOR_VARIANTS];

type TriangleIndicatorColours = {
  triangleIndicatorColours: Record<TriangleIndicatorVariant, ColourAssignment>;
};

export const triangleIndicatorColours = ({ trading, base }: IColoursCollection): TriangleIndicatorColours => ({
  triangleIndicatorColours: {
    [TRIANGLE_INDICATOR_VARIANTS.POSITIVE]: {
      background: trading.positive
    },
    [TRIANGLE_INDICATOR_VARIANTS.NEGATIVE]: {
      background: trading.negative
    },
    [TRIANGLE_INDICATOR_VARIANTS.WHITE]: {
      background: base.white.colour
    },
    [TRIANGLE_INDICATOR_VARIANTS.BLACK]: {
      background: base.black.colour
    }
  }
});

export type TriangleIndicatorSize =
  | typeof SIZES.XX_MICRO
  | typeof SIZES.X_MICRO
  | typeof SIZES.MICRO
  | typeof SIZES.XXX_SMALL
  | typeof SIZES.XX_SMALL
  | typeof SIZES.X_SMALL
  | typeof SIZES.SMALL
  | typeof SIZES.MEDIUM
  | typeof SIZES.LARGE
  | typeof SIZES.X_LARGE;

export interface ITriangleIndicatorProps extends IGutterAsMarginProps {
  negative?: boolean;
  size?: TriangleIndicatorSize;
  colour?: typeof TRIANGLE_INDICATOR_VARIANTS.WHITE | typeof TRIANGLE_INDICATOR_VARIANTS.BLACK;
}

export const TriangleIndicator = styled(BaseImg).attrs<ITriangleIndicatorProps>(({ size = DEFAULT_ICON_SIZE, negative, ...rest }) => {
  const { generateTestId } = useTestId(rest, `TriangleIndicator`);
  const src = useAssetIconUrl(negative ? MATERIAL_ICONS.DeltaDown : MATERIAL_ICONS.DeltaUp);

  return {
    src,
    size,
    ...rest,
    ...generateTestId(negative ? TRADING_VARIANTS.NEGATIVE : TRADING_VARIANTS.POSITIVE)
  };
})<ITriangleIndicatorProps>`
  ${useIconBaseCss};
  ${({ theme, colour, negative }) =>
    useFilterColourCss(theme.triangleIndicatorColours[colour ?? (negative ? TRADING_VARIANTS.NEGATIVE : TRADING_VARIANTS.POSITIVE)].background)}
`;
